import './instrument';

import { MantineProvider } from '@mantine/core';
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import { ModalsProvider } from '@mantine/modals';
import { Notifications } from '@mantine/notifications';
import '@mantine/notifications/styles.css';
import '@mantine/tiptap/styles.css';
import { FEATURE_FLAGS } from '@bariendo/lib/constants';
import { MedplumClient } from '@medplum/core';
import { MedplumProvider } from '@medplum/react';
import '@medplum/react/styles.css';
import * as Sentry from '@sentry/react';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import 'mantine-react-table/styles.css';
import posthog from 'posthog-js';
import { PostHogProvider, useFeatureFlagEnabled } from 'posthog-js/react';
import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import 'react-h5-audio-player/lib/styles.css';
import { createBrowserRouter } from 'react-router';
import { RouterProvider } from 'react-router/dom';
import { App } from './App';
import { getConfig } from './config';
import './index.css';
import { MaintenancePage } from './pages/MaintenancePage';
import { theme } from './theme/mantineTheme';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);

const container = document.getElementById('root') as HTMLDivElement;
const root = createRoot(container);
const router = createBrowserRouter([{ path: '*', element: <App /> }]);

export const AppContent = () => {
  const config = getConfig();
  const medplum = new MedplumClient({
    // Unset in every deployed build, so the client keeps its api.medplum.com default. Only the
    // local stack (everself-local-stack) sets it, to a throwaway Medplum server.
    ...(config.medplumBaseUrl && { baseUrl: config.medplumBaseUrl }),
    clientId: config.clientId,
    onUnauthenticated: () => (window.location.href = '/signin'),
    cacheTime: 5000,
  });

  return (
    <MedplumProvider medplum={medplum} navigate={router.navigate}>
      <MantineProvider theme={theme}>
        <ModalsProvider>
          <Notifications />
          <RouterProvider router={router} />
        </ModalsProvider>
      </MantineProvider>
    </MedplumProvider>
  );
};

const getFeatureFlagInterval = () => {
  const interval = Number(import.meta.env.VITE_FEATURE_FLAG_POLL_INTERVAL_MS);
  if (isNaN(interval) || interval === 0) {
    return 60_000;
  }
  return interval;
};

const FEATURE_FLAG_POLL_INTERVAL_MS = getFeatureFlagInterval();

function AppWithMaintenanceGuard() {
  const isMaintenanceMode = useFeatureFlagEnabled(FEATURE_FLAGS.MAINTENANCE_MODE);

  useEffect(() => {
    const interval = setInterval(() => {
      posthog.reloadFeatureFlags();
    }, FEATURE_FLAG_POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, []);

  if (isMaintenanceMode) {
    return (
      <MantineProvider theme={theme}>
        <MaintenancePage />
      </MantineProvider>
    );
  }

  return <AppContent />;
}

root.render(
  <StrictMode>
    <PostHogProvider client={posthog}>
      <Sentry.ErrorBoundary
        fallback={({ error, resetError }) => (
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <h2>Something went wrong:</h2>
            <pre>{error instanceof Error ? error.message : String(error)}</pre>
            <button onClick={resetError}>Try again</button>
          </div>
        )}
      >
        <AppWithMaintenanceGuard />
      </Sentry.ErrorBoundary>
    </PostHogProvider>
  </StrictMode>
);
